/**
 * Registers window.onbeforeunload for OTP verification flows so accidental tab close / refresh warns.
 * Clears the handler on intentional navigation (links, resend, workflow primary, or form submit events
 * from native submit buttons). Programmatic form.submit() does not fire a submit event — use
 * clearOtpBeforeUnload() in the caller or rely on [data-workflow-submit] click handling.
 */

/** @type {((e: BeforeUnloadEvent) => string | void) | null} */
let unloadFuncRef = null;

/** @type {HTMLFormElement | null} */
let boundForm = null;

/** @type {((e: Event) => void) | null} */
let formSubmitCaptureHandler = null;

/** @type {((e: MouseEvent) => void) | null} */
let documentClickHandler = null;

export function clearOtpBeforeUnload() {
    window.onbeforeunload = null;
}

/**
 * @param {string} deliveryMethod – email | sms | voice, etc.
 */
function buildUnloadMessage(deliveryMethod) {
    const channel = deliveryMethod === 'email' ? 'email' : 'phone';
    return (
        'You are about to leave the ' +
        channel +
        ' verification. If you proceed, the entry you made during the session will not be saved.'
    );
}

/**
 * @param {string} str
 */
function normalizeButtonLabel(str) {
    return str.replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * @param {Element | null} el
 */
function interactiveLabel(el) {
    if (!el) return '';
    return normalizeButtonLabel(el.textContent || '');
}

/**
 * @param {string} inputName – "Code" (MFA) or "securitycode" (setup/profile)
 */
function attachFormSubmitListener(inputName) {
    const escaped =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
            ? CSS.escape(inputName)
            : inputName.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const selector = `input[name="${escaped}"]`;
    const input = document.querySelector(selector);
    if (!input || !input.form) {
        return;
    }
    const form = input.form;

    if (boundForm === form && formSubmitCaptureHandler) {
        return;
    }

    if (boundForm && formSubmitCaptureHandler) {
        boundForm.removeEventListener('submit', formSubmitCaptureHandler, true);
    }

    boundForm = form;

    formSubmitCaptureHandler = function onFormSubmitCapture() {
        clearOtpBeforeUnload();
    };

    form.addEventListener('submit', formSubmitCaptureHandler, true);
}

/**
 * @param {string} deliveryMethod – email | sms | voice
 * @param {{ inputName: string }} options
 */
export function setupOtpBeforeUnload(deliveryMethod, options) {
    const { inputName } = options;

    unloadFuncRef = function unloadFunc(e) {
        const confirmationMessage = buildUnloadMessage(deliveryMethod);
        (e || window.event).returnValue = confirmationMessage;
        return confirmationMessage;
    };

    window.onbeforeunload = unloadFuncRef;

    attachFormSubmitListener(inputName);

    const scheduleAttach = () => attachFormSubmitListener(inputName);
    if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(scheduleAttach);
    }

    documentClickHandler = function onDocumentClick(e) {
        const target = /** @type {EventTarget | null} */ (e.target);
        if (!target || typeof target !== 'object' || !('closest' in target)) {
            return;
        }
        /** @type {Element} */
        const el = /** @type {Element} */ (target);

        if (el.closest('[data-workflow-submit]')) {
            clearOtpBeforeUnload();
            return;
        }

        const workflowNavControl = el.closest(
            '.mfa-login-action-container button, .mfa-login-action-container a'
        );
        if (workflowNavControl) {
            clearOtpBeforeUnload();
            return;
        }

        const interactive = el.closest('button, a');
        if (!interactive) {
            return;
        }

        const label = interactiveLabel(interactive);

        if (
            label === 'edit' ||
            label === 'cancel' ||
            label === 'previous' ||
            label === 'next' ||
            label === 'resend code' ||
            label === 'verify' ||
            label === 'try another option' ||
            label === 'verify & login' ||
            label.startsWith('logout')
        ) {
            clearOtpBeforeUnload();
        }
    };

    document.addEventListener('click', documentClickHandler, false);
}

export function teardownOtpBeforeUnload() {
    clearOtpBeforeUnload();
    unloadFuncRef = null;

    if (boundForm && formSubmitCaptureHandler) {
        boundForm.removeEventListener('submit', formSubmitCaptureHandler, true);
    }
    boundForm = null;
    formSubmitCaptureHandler = null;

    if (documentClickHandler) {
        document.removeEventListener('click', documentClickHandler, false);
        documentClickHandler = null;
    }
}
